/**
 * Core Semantic Diff Engine
 *
 * Compares two structured objects and generates a detailed diff with:
 * - Added, removed, modified, and type-changed values
 * - Full nested path tracking
 * - Type-aware comparisons
 */

export const ChangeType = {
  ADDED: 'added',
  REMOVED: 'removed',
  MODIFIED: 'modified',
  TYPE_CHANGED: 'typeChanged',
  UNCHANGED: 'unchanged'
};

/**
 * Get the type of a value with proper handling of arrays, objects, null
 */
function getType(value) {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

/**
 * Check if a value is a primitive (not an object or array)
 */
function isPrimitive(value) {
  const type = getType(value);
  return type !== 'object' && type !== 'array';
}

/**
 * Deep equality check for primitives and simple values
 */
function isEqual(val1, val2) {
  // Strict equality for primitives
  if (isPrimitive(val1) && isPrimitive(val2)) {
    return val1 === val2;
  }

  // If types differ, not equal
  if (getType(val1) !== getType(val2)) {
    return false;
  }

  // For arrays, check length and elements
  if (Array.isArray(val1)) {
    if (val1.length !== val2.length) return false;
    return val1.every((item, idx) => isEqual(item, val2[idx]));
  }

  // For objects, check keys and values
  if (typeof val1 === 'object' && val1 !== null) {
    const keys1 = Object.keys(val1);
    const keys2 = Object.keys(val2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every(key => isEqual(val1[key], val2[key]));
  }

  return val1 === val2;
}

/**
 * Create a diff entry
 */
function createDiffEntry(path, changeType, oldValue, newValue, oldType, newType) {
  return {
    path,
    changeType,
    oldValue,
    newValue,
    oldType,
    newType
  };
}

/**
 * Main diff function - recursively compares two objects
 *
 * @param {*} original - Original data structure
 * @param {*} modified - Modified data structure
 * @param {string} basePath - Current path in the object tree
 * @param {Array} diffs - Accumulator for diff entries
 * @returns {Array} Array of diff entries
 */
function diffRecursive(original, modified, basePath = '', diffs = []) {
  const origType = getType(original);
  const modType = getType(modified);

  // If types differ, it's a type change
  if (origType !== modType) {
    diffs.push(createDiffEntry(
      basePath || '(root)',
      ChangeType.TYPE_CHANGED,
      original,
      modified,
      origType,
      modType
    ));
    return diffs;
  }

  // Handle null/undefined
  if (original === null || original === undefined) {
    if (modified !== null && modified !== undefined) {
      diffs.push(createDiffEntry(
        basePath || '(root)',
        ChangeType.ADDED,
        original,
        modified,
        origType,
        modType
      ));
    }
    return diffs;
  }

  // Handle primitives
  if (isPrimitive(original)) {
    if (!isEqual(original, modified)) {
      diffs.push(createDiffEntry(
        basePath || '(root)',
        ChangeType.MODIFIED,
        original,
        modified,
        origType,
        modType
      ));
    }
    return diffs;
  }

  // Handle arrays
  if (Array.isArray(original)) {
    const maxLength = Math.max(original.length, modified.length);

    for (let i = 0; i < maxLength; i++) {
      const currentPath = basePath ? `${basePath}[${i}]` : `[${i}]`;

      if (i >= original.length) {
        // Added in modified
        diffs.push(createDiffEntry(
          currentPath,
          ChangeType.ADDED,
          undefined,
          modified[i],
          'undefined',
          getType(modified[i])
        ));
      } else if (i >= modified.length) {
        // Removed from original
        diffs.push(createDiffEntry(
          currentPath,
          ChangeType.REMOVED,
          original[i],
          undefined,
          getType(original[i]),
          'undefined'
        ));
      } else {
        // Recursively compare array elements
        diffRecursive(original[i], modified[i], currentPath, diffs);
      }
    }

    return diffs;
  }

  // Handle objects
  if (typeof original === 'object') {
    const allKeys = new Set([
      ...Object.keys(original),
      ...Object.keys(modified)
    ]);

    for (const key of allKeys) {
      const currentPath = basePath ? `${basePath}.${key}` : key;
      const hasOriginal = key in original;
      const hasModified = key in modified;

      if (!hasOriginal && hasModified) {
        // Key added
        diffs.push(createDiffEntry(
          currentPath,
          ChangeType.ADDED,
          undefined,
          modified[key],
          'undefined',
          getType(modified[key])
        ));
      } else if (hasOriginal && !hasModified) {
        // Key removed
        diffs.push(createDiffEntry(
          currentPath,
          ChangeType.REMOVED,
          original[key],
          undefined,
          getType(original[key]),
          'undefined'
        ));
      } else {
        // Both exist, recursively compare
        diffRecursive(original[key], modified[key], currentPath, diffs);
      }
    }

    return diffs;
  }

  return diffs;
}

/**
 * Public API: Generate semantic diff between two data structures
 *
 * @param {*} original - Original data
 * @param {*} modified - Modified data
 * @returns {Object} Diff result with statistics and changes
 */
export function generateDiff(original, modified) {
  const diffs = diffRecursive(original, modified);

  // Calculate statistics
  const stats = {
    total: diffs.length,
    added: diffs.filter(d => d.changeType === ChangeType.ADDED).length,
    removed: diffs.filter(d => d.changeType === ChangeType.REMOVED).length,
    modified: diffs.filter(d => d.changeType === ChangeType.MODIFIED).length,
    typeChanged: diffs.filter(d => d.changeType === ChangeType.TYPE_CHANGED).length
  };

  return {
    diffs,
    stats,
    hasChanges: diffs.length > 0
  };
}

/**
 * Format value for display (truncate if too long)
 */
export function formatValue(value, maxLength = 100) {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  const type = getType(value);

  if (type === 'string') {
    if (value.length > maxLength) {
      return `"${value.substring(0, maxLength)}..."`;
    }
    return `"${value}"`;
  }

  if (type === 'array' || type === 'object') {
    const str = JSON.stringify(value, null, 2);
    if (str.length > maxLength) {
      return `${str.substring(0, maxLength)}...`;
    }
    return str;
  }

  return String(value);
}

/**
 * Export diff as human-readable text
 */
export function exportDiffAsText(diffResult) {
  const { diffs, stats } = diffResult;

  let output = '=== SEMANTIC DIFF SUMMARY ===\n\n';
  output += `Total Changes: ${stats.total}\n`;
  output += `Added: ${stats.added}\n`;
  output += `Removed: ${stats.removed}\n`;
  output += `Modified: ${stats.modified}\n`;
  output += `Type Changed: ${stats.typeChanged}\n\n`;
  output += '=== DETAILED CHANGES ===\n\n';

  diffs.forEach((diff, idx) => {
    output += `${idx + 1}. ${diff.path}\n`;
    output += `   Change: ${diff.changeType.toUpperCase()}\n`;

    if (diff.changeType === ChangeType.TYPE_CHANGED) {
      output += `   Old Type: ${diff.oldType}\n`;
      output += `   New Type: ${diff.newType}\n`;
    }

    if (diff.oldValue !== undefined) {
      output += `   Old: ${formatValue(diff.oldValue)}\n`;
    }

    if (diff.newValue !== undefined) {
      output += `   New: ${formatValue(diff.newValue)}\n`;
    }

    output += '\n';
  });

  return output;
}
